// client/src/pages/POA/StaffNameInput.tsx
// Modal-based representative picker. User must select from search results —
// free-text entry is not allowed because the name drives the credential lookup
// (legal name, CAF, PTIN, license #).
//
// Results are PE staff plus any stored contractors (see the Contractors tab).
// Contractors are listed because the picker refuses free text: an outside
// representative with no PE staff record could not otherwise be chosen at all.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

export interface PEStaff {
  staffName:    string;
  staffUser:    string;
  staffOffice:  string;
  isContractor?: boolean;
}

interface Props {
  value:     string;
  hasError?: boolean;
  onAccept:  (staff: PEStaff) => void;
  onChange:  (name: string)  => void;   // kept for clearing
}

async function fetchStaff(q: string): Promise<PEStaff[]> {
  const res = await fetch(`/api/pe-staff-search?q=${encodeURIComponent(q)}`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.staff ?? [];
}

export default function StaffNameInput({ value, hasError, onAccept }: Props) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      {/* Read-only trigger — click opens picker modal */}
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        style={{
          width: '100%', padding: '8px 10px', borderRadius: 8, textAlign: 'left',
          border: `1px solid ${hasError ? 'var(--bad)' : 'var(--border)'}`,
          background: 'rgba(255,255,255,.04)', color: value ? 'var(--text)' : 'var(--muted)',
          fontSize: 14, fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box',
        }}
      >
        {value || 'Click to search staff...'}
      </button>

      {modalOpen && (
        <StaffPickerModal
          onSelect={(staff) => { setModalOpen(false); onAccept(staff); }}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────

function StaffPickerModal({ onSelect, onClose }: {
  onSelect: (staff: PEStaff) => void;
  onClose:  () => void;
}) {
  const [query,       setQuery]       = useState('');
  const [results,     setResults]     = useState<PEStaff[]>([]);
  const [loading,     setLoading]     = useState(false);
  const [searched,    setSearched]    = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef   = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-focus search input on open
  useEffect(() => { inputRef.current?.focus(); }, []);

  const runSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); setSearched(false); setLoading(false); return; }
    setLoading(true);
    try {
      const data = await fetchStaff(q);
      setResults(data);
      setHighlighted(0);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val.trim()), 300);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') { onClose(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onSelect(results[highlighted]);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '12vh',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--surface, #1e2030)', border: '1px solid var(--border)',
        borderRadius: 16, width: 480, maxWidth: '94vw',
        boxShadow: '0 20px 60px rgba(0,0,0,.7)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Select Representative</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
              Search for a staff member by name. Selection is required to look up signer details
              (legal name, CAF, PTIN, license #).
            </div>
          </div>
          <button
            type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Search input */}
        <div style={{ padding: '16px 24px 12px', position: 'relative' }}>
          <Search size={15} style={{ position: 'absolute', left: 36, top: 28, color: 'var(--muted)' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a name to search..."
            autoComplete="off"
            style={{
              width: '100%', padding: '10px 12px 10px 36px', borderRadius: 10,
              border: '1px solid var(--border)', background: 'rgba(255,255,255,.04)',
              color: 'var(--text)', fontSize: 14, outline: 'none', fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Results */}
        <div style={{ maxHeight: 300, overflowY: 'auto', padding: '0 12px 12px' }}>
          {loading && (
            <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              Searching...
            </div>
          )}

          {!loading && searched && results.length === 0 && (
            <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              No representatives found for "{query}"
            </div>
          )}

          {!loading && results.map((s, i) => (
            <button
              key={s.staffUser || s.staffName}
              type="button"
              onClick={() => onSelect(s)}
              onMouseEnter={() => setHighlighted(i)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', textAlign: 'left',
                padding: '11px 14px', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 14, borderRadius: 8,
                marginBottom: 2,
                background: i === highlighted ? 'rgba(122,162,255,.12)' : 'transparent',
                color: 'var(--text)',
              }}
            >
              <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                {s.staffName}
                {s.isContractor && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: .4, textTransform: 'uppercase',
                    padding: '2px 7px', borderRadius: 20,
                    background: 'rgba(224,175,104,.12)', color: '#e0af68',
                    border: '1px solid rgba(224,175,104,.3)',
                  }}>
                    Contractor
                  </span>
                )}
              </span>
              {s.staffOffice && (
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{s.staffOffice}</span>
              )}
            </button>
          ))}
        </div>

        {/* Footer hint */}
        {results.length > 0 && (
          <div style={{
            padding: '8px 24px 14px', fontSize: 11, color: 'var(--muted)',
            borderTop: '1px solid var(--border)',
          }}>
            Click a name or use ↑↓ and Enter to select
          </div>
        )}
      </div>
    </div>
  );
}
